// Common schemas used across the model
// Based on ADR-0002 (Schema-First Validation with Zod)

import { z } from 'zod';

// ============================================================================
// Units and Values
// ============================================================================

/**
 * A value with a unit (e.g., "100 m", "50 kg/s")
 * Used throughout the model for physical quantities
 */
export const ValueWithUnitSchema = z.object({
  value: z.number(),
  unit: z.string(),
});

export type ValueWithUnit = z.infer<typeof ValueWithUnitSchema>;

// ============================================================================
// Diagram Properties (ADR-0004: Diagram Properties Namespacing)
// ============================================================================

/**
 * Base diagram properties for visual rendering
 * All UI-related properties are namespaced under 'diagram'
 * This separates display concerns from calculation data
 */
/**
 * Where a moved results readout sits, as a world-unit delta from its DEFAULT anchor.
 *
 * ⚠️ Relative to the anchor, never to a resolved on-screen position. Storing an absolute
 * point would break the moment anything else moves the label's baseline — the owning device
 * being dragged, a font change, or a future auto-declutter pass — and the user's placement
 * would silently drift. Absent means "wherever the renderer puts it", which is the default
 * for every element that has never been moved.
 */
export const ResultsOffsetSchema = z.object({
  dx: z.number(),
  dy: z.number(),
});

export type ResultsOffset = z.infer<typeof ResultsOffsetSchema>;

export const DiagramPropertiesSchema = z.object({
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  // Results readout nudged clear of a crowded area. See ResultsOffsetSchema.
  resultsOffset: ResultsOffsetSchema.optional(),
  /**
   * A flo.shape symbol NAME chosen for this device, e.g. 'ISO_Screw_Compressor'.
   * Absent means draw the default for the device type.
   *
   * The category is not stored: it follows from the device type, and storing
   * both would let them disagree. Names are unique within a category.
   *
   * Connect-native. PIPE-FLO records the same choice in the .pipe file, but the
   * C++ converter does not carry it into schema v2, so an imported model always
   * arrives with this absent — a desktop user's choice cannot be honoured until
   * the converter emits it. Export writes it back out, so a choice made here does
   * reach desktop.
   */
  symbol: z.string().optional(),
});

export type DiagramProperties = z.infer<typeof DiagramPropertiesSchema>;

// ============================================================================
// v2: Port Pinning
// ============================================================================

/** The four device connection sides a pipe end can be pinned to. */
export const PortSideSchema = z.enum(['top', 'right', 'bottom', 'left']);
export type PortSide = z.infer<typeof PortSideSchema>;

/**
 * Edge (pipe) diagram properties
 * Stores the visual path for rendering pipes
 */
export const EdgeDiagramPropertiesSchema = z.object({
  points: z.array(
    z.object({
      x: z.number(),
      y: z.number(),
    })
  ),
  // React Flow handle IDs for connection routing
  sourceHandle: z.string().optional(),
  targetHandle: z.string().optional(),
  // Port pinning: when set, the named end attaches to this device side and the
  // router uses it instead of choosing a side from geometry. Absent = auto.
  sourcePin: PortSideSchema.optional(),
  targetPin: PortSideSchema.optional(),
  // Waypoints (Phase 2): the ordered corner chain of a user-shaped route
  // (world coordinates), edited by sliding pipe segments. The router keeps
  // this chain verbatim and recomputes only the two end legs, so the shaped
  // route survives device moves. Absent = fully auto-routed. `points` remains
  // the DERIVED render polyline; `waypoints` is the persisted intent.
  // Invariant (not validated by schema): consecutive entries share x or y exactly.
  waypoints: z.array(z.object({ x: z.number(), y: z.number() })).optional(), // inline point shape: PointSchema is declared later in this file (TDZ)
  // Results readout nudged clear of a crowded area. Same contract as the vertex side —
  // a delta from the default anchor, absent when never moved. See ResultsOffsetSchema.
  resultsOffset: ResultsOffsetSchema.optional(),
});

export type EdgeDiagramProperties = z.infer<typeof EdgeDiagramPropertiesSchema>;

// ============================================================================
// v2: Design Status
// ============================================================================

export const DesignStatusSchema = z.enum(['undesigned', 'sizing', 'designed']);

export type DesignStatus = z.infer<typeof DesignStatusSchema>;

// ============================================================================
// v2: Pressure Drop Curves
// ============================================================================

/**
 * A point on a pressure-drop vs flow-rate curve.
 * Used by: curve_loss, pressure_gain, heat_source_sink, heat_exchanger.
 */
export const PressureDropCurvePointSchema = z.object({
  flow: ValueWithUnitSchema,
  pressure_drop: ValueWithUnitSchema,
});

export type PressureDropCurvePoint = z.infer<typeof PressureDropCurvePointSchema>;

export const PressureDropCurveSchema = z.object({
  description: z.string().optional(),
  points: z.array(PressureDropCurvePointSchema),
});

export type PressureDropCurve = z.infer<typeof PressureDropCurveSchema>;

// ============================================================================
// v2: Meter Materials
// ============================================================================

export const MeterMaterialSchema = z.enum([
  'alloy_steel', 'aluminum', 'brass', 'bronze', 'carbon_steel',
  'crni_25_20', 'cuni_70_30', 'monel_66_29', 'monel_67_30',
  'series_300_ss', 'series_400_ss', 'wrought_iron',
]);

export type MeterMaterial = z.infer<typeof MeterMaterialSchema>;

// ============================================================================
// v2: Pressure Settings (compressible flow devices)
// ============================================================================

export const PressureTypeSchema = z.enum(['total_pressure', 'static_pressure']);

export type PressureType = z.infer<typeof PressureTypeSchema>;

export const PressureSettingSchema = z.object({
  pressure_type: PressureTypeSchema,
  is_differential: z.boolean(),
  total_pressure: ValueWithUnitSchema.optional(),
  static_pressure: ValueWithUnitSchema.optional(),
  total_pressure_dP: ValueWithUnitSchema.optional(),
  static_pressure_dP: ValueWithUnitSchema.optional(),
});

export type PressureSetting = z.infer<typeof PressureSettingSchema>;

// ============================================================================
// v2: RGBA Color (used by annotations and gradients)
// ============================================================================

export const RgbaSchema = z.object({
  red: z.number(),
  green: z.number(),
  blue: z.number(),
  alpha: z.number(),
});

export type Rgba = z.infer<typeof RgbaSchema>;

// ============================================================================
// v2: Point (used by annotations)
// ============================================================================

export const PointSchema = z.object({ x: z.number(), y: z.number() });

export type Point = z.infer<typeof PointSchema>;

